<template>
  <header class="global-header" :class="{ 'is-open': mobileOpen }">
    <router-link to="/" class="logo-link" @click.native="closeMobile">
      <img src="/images/brand/scott-noles-lockup.png" alt="SCOTT NOLES" />
    </router-link>

    <!-- 桌面导航 -->
    <nav class="desktop-nav" :aria-label="t('nav.mainNav')">
      <div class="nav-cluster">
        <router-link to="/products">{{ t('nav.products') }}</router-link>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 9 6 6 6-6"/></svg>
        <!-- Mega Menu -->
        <div class="mega-menu">
          <div class="mega-intro">
            <span>PRODUCTS</span>
            <strong>{{ t('nav.megaIntro') }}</strong>
            <router-link to="/products">{{ t('common.allProducts') }} <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg></router-link>
          </div>
          <div class="mega-categories">
            <div class="mega-category" v-for="cat in categories" :key="cat.id">
              <router-link :to="'/products/' + cat.slug" @click.native="closeMobile">
                <span>{{ tfAlt(cat, 'name') }}</span>
                <b>{{ tf(cat, 'name') }}</b>
              </router-link>
              <div>
                <router-link v-for="p in productsOf(cat)" :key="p.id" :to="'/products/' + p.categorySlug + '/' + p.slug" @click.native="closeMobile">{{ tf(p, 'name') }}</router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <router-link to="/gallery">{{ t('nav.gallery') }}</router-link>
      <router-link to="/colors">{{ t('nav.colors') }}</router-link>
      <router-link to="/insights">{{ t('nav.insights') }}</router-link>
      <router-link to="/about">{{ t('nav.about') }}</router-link>
      <div class="nav-cluster">
        <router-link to="/warranty">{{ t('nav.warranty') }}</router-link>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 9 6 6 6-6"/></svg>
        <div class="mini-menu service-menu">
          <router-link to="/warranty/query">{{ t('nav.warrantyQuery') }}</router-link>
          <router-link to="/warranty/authentication">{{ t('nav.auth') }}</router-link>
          <router-link to="/warranty/store">{{ t('nav.storeQuery') }}</router-link>
          <router-link to="/warranty">{{ t('nav.policy') }}</router-link>
        </div>
      </div>
    </nav>

    <div class="header-actions">
      <div class="lang-switch">
        <button :class="{ active: lang === 'zh' }" @click="switchLang('zh')">中</button>
        <span class="divider">/</span>
        <button :class="{ active: lang === 'en' }" @click="switchLang('en')">EN</button>
      </div>
      <router-link to="/contact" class="header-contact">{{ t('nav.contact') }}</router-link>
      <button class="mobile-menu-button" @click="mobileOpen = !mobileOpen" :aria-label="t('nav.menu')">
        <svg v-if="!mobileOpen" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 7h16M4 12h16M4 17h16"/></svg>
        <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6 6 18"/></svg>
      </button>
    </div>

    <!-- 移动端导航 -->
    <div class="mobile-navigation" v-if="mobileOpen">
      <router-link class="mobile-nav-link" to="/" @click.native="closeMobile">{{ t('nav.home') }}</router-link>
      <div class="mobile-nav-group" v-for="cat in categories" :key="cat.id">
        <strong>{{ tf(cat, 'name') }}</strong>
        <router-link v-for="p in productsOf(cat)" :key="p.id" :to="'/products/' + p.categorySlug + '/' + p.slug" @click.native="closeMobile">{{ tf(p, 'name') }}</router-link>
        <router-link :to="'/products/' + cat.slug" @click.native="closeMobile">{{ t('nav.viewAllOf', { name: tf(cat, 'name') }) }}</router-link>
      </div>
      <router-link class="mobile-nav-link" to="/gallery" @click.native="closeMobile">{{ t('nav.gallery') }}</router-link>
      <router-link class="mobile-nav-link" to="/colors" @click.native="closeMobile">{{ t('nav.colors') }}</router-link>
      <router-link class="mobile-nav-link" to="/insights" @click.native="closeMobile">{{ t('nav.insights') }}</router-link>
      <router-link class="mobile-nav-link" to="/warranty" @click.native="closeMobile">{{ t('nav.warranty') }}</router-link>
      <router-link class="mobile-nav-link" to="/about" @click.native="closeMobile">{{ t('nav.about') }}</router-link>
      <router-link class="mobile-nav-link" to="/member/login" @click.native="closeMobile">{{ t('nav.member') }}</router-link>
      <div class="mobile-cta">
        <router-link to="/contact" class="dark-button" @click.native="closeMobile">{{ t('nav.contact') }}</router-link>
      </div>
    </div>
  </header>
</template>

<script>
import { getOverview } from '../api/content'
import { categories as fallbackCategories, products as fallbackProducts } from '../data/site-data'
import { getLang, switchLang, langMixin } from '../utils/site-lang'

export default {
  name: 'GlobalHeader',
  mixins: [langMixin],
  data () {
    return {
      categories: [],
      products: [],
      mobileOpen: false,
      lang: getLang()
    }
  },
  created () {
    this.loadOverview()
  },
  methods: {
    async loadOverview () {
      try {
        const data = await getOverview()
        if (!data || !data.categories) throw new Error('empty')
        this.categories = data.categories
        const slugOf = id => (this.categories.find(c => c.id === id) || {}).slug || ''
        this.products = (data.products || []).map(p => ({ ...p, categorySlug: slugOf(p.categoryId) }))
      } catch (e) {
        // 接口不可用时回退静态数据
        this.categories = fallbackCategories
        this.products = fallbackProducts
      }
    },
    // 匹配该分类下的产品：接口数据按 categoryId，静态兜底数据 id 即 slug、按 categorySlug 匹配
    productsOf (cat) {
      const id = cat.id
      return this.products
        .filter(p => p.categoryId === id || p.categorySlug === id || p.categorySlug === cat.slug)
        .slice(0, 3)
    },
    switchLang,
    closeMobile () {
      this.mobileOpen = false
    }
  }
}
</script>

<style scoped>
.lang-switch {
  display: flex;
  align-items: center;
  margin-right: 16px;
  font-size: 13px;
}
.lang-switch button {
  background: none;
  border: none;
  padding: 4px 6px;
  cursor: pointer;
  color: inherit;
  font-size: 13px;
  opacity: 0.5;
}
.lang-switch button.active {
  opacity: 1;
  font-weight: 700;
}
.lang-switch .divider {
  opacity: 0.3;
}
</style>
